import React, { useEffect, useState } from "react";
import { Button, Card, CardBody, Col, Form, Row } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { PackagesService } from "./service";
import { ErrorToast, SuccessToast } from "../../shared/toasters/toasters";
import { Switch } from "../../shared/components/Switch";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import { multipartFormData } from "../../helpers/api.services";
import { PACKAGES_URL } from "../../helpers/api.routes";
import FormDropzone from "../../shared/dropzone";
import { Editor } from "@tinymce/tinymce-react";
import FormErrorSummary from "../../shared/components/FormErrorSummary";

const schema = yup.object().shape({
  name: yup.string().required('formErrors.nameRequired'),
  type: yup.string().required('formErrors.typeRequired'),
  isActive: yup.boolean().required('formErrors.activeStatusRequired'),
  price: yup
    .number()
    .required("formErrors.priceRequired")
    .min(0, "formErrors.priceMin"),
  yearlyPrice: yup
    .number()
    .required("formErrors.yearlyPriceRequired")
    .min(0, "formErrors.yearlyPriceMin"),
  stripeLinkMonthly: yup
    .string()
    .required("formErrors.stripeMonthlyLinkRequired"),
  stripeLinkYearly: yup
    .string()
    .required("formErrors.stripeYearlyLinkRequired"),
  position: yup
    .number()
    .typeError("formErrors.positionMustBeNumber")
    .required("formErrors.positionRequired")
    .min(0, "formErrors.positionMin"),
  userSeeReview: yup.boolean(),
  userInformOnEmailIfMatchingJobs: yup.boolean(),
  userHaveVideoPresentation: yup.boolean(),
  userShowFittingJobs: yup.boolean(),
  companyCanCommunicateWithCoach: yup.boolean(),
  companySeeReview: yup.boolean(),
  companySeeCoachesHistory: yup.boolean(),
  companyCanAddLogo: yup.boolean(),
  companyMaxActiveJobs: yup
    .number()
    .typeError("formErrors.companyMaxActiveJobs"),
  companyMaxUsers: yup
    .number()
    .typeError("formErrors.companyMaxUsers"),
  companyMaxFeaturedJobs: yup
    .number()
    .typeError("formErrors.companyMaxFeaturedJobs"),
  companyMaxJobHistory: yup
    .number()
    .typeError("formErrors.companyMaxJobHistory"),
  stripeProductId: yup.string().required("formErrors.stripeProductIdRequired"),
  picture: yup.mixed(),
  companyCanCopyJob: yup.boolean(),
  allowedMultiposting: yup.boolean(),
  userShowJobHistory: yup.boolean(),
  userCanUploadDocuments: yup.boolean(),
  companyCanRequestDocuments: yup.boolean(),
  userFeaturedInCategoriesAndLocations: yup.boolean(),
  shortDescription: yup.string(),
  description: yup.string(),
  isRecommended: yup.boolean().default(false),
});

interface IPicture {
  id?: number;
  path?: string;
  file?: {
    name: string;
    nameOriginal: string;
    mime: string;
    size: number
  }
}

interface PackagesState {
  name: string;
  type: string;
  isActive: boolean;
  price: number;
  yearlyPrice: number;
  stripeLinkMonthly: string;
  stripeLinkYearly: string;
  position: number;
  isRecommended: boolean;
  userSeeReview?: boolean;
  userInformOnEmailIfMatchingJobs?: boolean;
  userHaveVideoPresentation?: boolean;
  userShowFittingJobs?: boolean;
  companyCanCommunicateWithCoach?: boolean;
  companySeeReview?: boolean;
  companySeeCoachesHistory?: boolean;
  companyCanAddLogo?: boolean;
  companyMaxActiveJobs?: number;
  companyMaxUsers?: number;
  companyMaxFeaturedJobs?: number;
  companyMaxJobHistory?: number; // <-- Added
  stripeProductId: string;
  pictures?: IPicture[],
  companyCanCopyJob?: boolean;
  allowedMultiposting?: boolean;
  userShowJobHistory?: boolean;
  userCanUploadDocuments?: boolean;
  companyCanRequestDocuments?: boolean;
  userFeaturedInCategoriesAndLocations?: boolean;
  shortDescription?: string;
  description?: string;
}

const PackagesForm: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [files, setFiles] = useState<File[] | []>([]);
  const {
    control,
    handleSubmit,
    reset,
    watch,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<PackagesState>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: "",
      type: "",
      isActive: true,
      price: 0,
      yearlyPrice: 0,
      stripeLinkMonthly: "",
      stripeLinkYearly: "",
      stripeProductId: "",
      isRecommended: false,
      position: 0,
      userSeeReview: false,
      userInformOnEmailIfMatchingJobs: false,
      userHaveVideoPresentation: false,
      userShowFittingJobs: false,
      companyCanCommunicateWithCoach: false,
      companySeeReview: false,
      companySeeCoachesHistory: false,
      companyCanAddLogo: false,
      companyMaxActiveJobs: 0,
      companyMaxUsers: 0,
      companyMaxFeaturedJobs: 0,
      companyMaxJobHistory: 0,
      pictures: [],
      userShowJobHistory: false,
      userFeaturedInCategoriesAndLocations: false,
      userCanUploadDocuments: false,
      companyCanRequestDocuments: false,
      shortDescription: "",
      description: "",
    },
  });

  const selectedType = watch("type");

  const handleFileUpload = (files: File[]) => {
    setFiles(files);
  };
  useEffect(() => {
    if (isEdit) {
      const fetchPackage = async () => {
        try {
          const response = await PackagesService.getSingle(+id!);
          reset(response);
        } catch (error) {
          ErrorToast(error);
        }
      };

      fetchPackage();
    }
  }, [isEdit, id, reset]);

  // Reset state for the other type when switching
  useEffect(() => {
    if (selectedType === "user") {
      setValue("companyCanCommunicateWithCoach", false);
      setValue("companySeeReview", false);
      setValue("companySeeCoachesHistory", false);
      setValue("companyCanAddLogo", false);
      setValue("companyMaxActiveJobs", 0);
      setValue("companyMaxUsers", 0);
      setValue("companyMaxFeaturedJobs", 0);
      setValue("companyCanRequestDocuments", false);
    } else if (selectedType === "company") {
      setValue("userSeeReview", false);
      setValue("userInformOnEmailIfMatchingJobs", false);
      setValue("userHaveVideoPresentation", false);
      setValue("userShowFittingJobs", false);
      setValue("userCanUploadDocuments", false);
    }
  }, [selectedType, setValue]);

  const onSubmit = async (data: PackagesState) => {
    // try {
    //   // Convert the type to lowercase before sending the data
    //   const payload = {
    //     ...data,
    //     type: data.type.toLowerCase(),
    //   };
    //
    //   isEdit
    //     ? await PackagesService.update(payload, +id!)
    //     : await PackagesService.create(payload);
    //
    //   SuccessToast(t(`packages.${isEdit ? "successUpdate" : "successCreate"}`));
    //   navigate("/packages");
    //   reset();
    // } catch (error) {
    //   ErrorToast(error);
    // }
    const url = isEdit ? `${PACKAGES_URL}/${id}` : PACKAGES_URL;
    const method = isEdit ? "put" : "post";

    multipartFormData(
      files,
      url,
      JSON.stringify(data),
      method,
      'file'
    )
      .then(() => {
        SuccessToast(t(`packages.${isEdit ? 'successUpdate' : 'successCreate'}`));
        navigate('/packages');
        reset();
      })
      .catch(ErrorToast);

  };
  return (
    <React.Fragment>
      <Row>
        <Col sm={12}>
          <Card>
            <CardBody>
              <Form onSubmit={handleSubmit(onSubmit)}>
                <FormErrorSummary errors={errors} />
                <Row className="g-3">
                  {/* Name Field */}
                  <Col sm={12} xl={6}>
                    <div className="mb-0">
                      <label className="form-label">{t("packages.name")}</label>
                      <Controller
                        name="name"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="text"
                            className={`form-control ${errors.name ? "is-invalid" : ""
                              }`}
                            placeholder={t("packages.name")}
                            {...field}
                          />
                        )}
                      />
                      {errors.name && (
                        <div className="invalid-feedback">
                          {t(errors.name.message as string)}
                        </div>
                      )}
                    </div>
                  </Col>

                  {/* Type Field */}
                  <Col sm={12} xl={6}>
                    <div className="mb-0">
                      <label className="form-label">{t("packages.type")}</label>
                      <Controller
                        name="type"
                        control={control}
                        render={({ field }) => (
                          <Select
                            id="type"
                            classNamePrefix="react-select"
                            className={`react-select-container ${errors.type ? "is-invalid" : ""
                              }`}
                            value={
                              field.value
                                ? {
                                  label:
                                    field.value.charAt(0).toUpperCase() +
                                    field.value.slice(1),
                                  value: field.value,
                                }
                                : null
                            }
                            onChange={(selectedOption) =>
                              field.onChange(
                                selectedOption ? selectedOption.value : ""
                              )
                            }
                            options={[
                              { label: "User", value: "user" },
                              { label: "Company", value: "company" },
                            ]}
                          />
                        )}
                      />
                      {errors.type && (
                        <div className="invalid-feedback">
                          {t(errors.type.message as string)}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col sm={12} xl={6}>
                    <div className="mb-0">
                      <label className="form-label">
                        {t("packages.stripeProductId")}
                      </label>
                      <Controller
                        name="stripeProductId"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="text"
                            className={`form-control ${errors.stripeProductId ? "is-invalid" : ""
                              }`}
                            placeholder={t("packages.stripeProductId")}
                            {...field}
                          />
                        )}
                      />
                      {errors.stripeProductId && (
                        <div className="invalid-feedback">
                          {t(errors.stripeProductId.message as string)}
                        </div>
                      )}
                    </div>
                  </Col>

                  {/* Price and Stripe Monthly Link */}
                  <Col sm={12} xl={6}>
                    <div className="mb-0">
                      <label className="form-label">
                        {t("packages.price")}
                      </label>
                      <Controller
                        name="price"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="number"
                            className={`form-control ${errors.price ? "is-invalid" : ""
                              }`}
                            placeholder={t("packages.price")}
                            {...field}
                          />
                        )}
                      />
                      {errors.price && (
                        <div className="invalid-feedback">
                          {t(errors.price.message as string)}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col sm={12} xl={6}>
                    <div className="mb-0">
                      <label className="form-label">
                        {t("packages.stripeProductPriceIdMonthly")}
                      </label>
                      <Controller
                        name="stripeLinkMonthly"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="text"
                            className={`form-control ${errors.stripeLinkMonthly ? "is-invalid" : ""
                              }`}
                            placeholder={t(
                              "packages.stripeProductPriceIdMonthly"
                            )}
                            {...field}
                          />
                        )}
                      />
                      {errors.stripeLinkMonthly && (
                        <div className="invalid-feedback">
                          {t(errors.stripeLinkMonthly.message as string)}
                        </div>
                      )}
                    </div>
                  </Col>

                  {/* Yearly Price and Stripe Yearly Link */}
                  <Col sm={12} xl={6}>
                    <div className="mb-0">
                      <label className="form-label">
                        {t("packages.yearlyPrice")}
                      </label>
                      <Controller
                        name="yearlyPrice"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="number"
                            className={`form-control ${errors.yearlyPrice ? "is-invalid" : ""
                              }`}
                            placeholder={t("packages.yearlyPrice")}
                            {...field}
                          />
                        )}
                      />
                      {errors.yearlyPrice && (
                        <div className="invalid-feedback">
                          {t(errors.yearlyPrice.message as string)}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col sm={12} xl={6}>
                    <div className="mb-0">
                      <label className="form-label">
                        {t("packages.stripeProductPriceIdYearly")}
                      </label>
                      <Controller
                        name="stripeLinkYearly"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="text"
                            className={`form-control ${errors.stripeLinkYearly ? "is-invalid" : ""
                              }`}
                            placeholder={t(
                              "packages.stripeProductPriceIdYearly"
                            )}
                            {...field}
                          />
                        )}
                      />
                      {errors.stripeLinkYearly && (
                        <div className="invalid-feedback">
                          {t(errors.stripeLinkYearly.message as string)}
                        </div>
                      )}
                    </div>
                  </Col>

                  {/* Position Field */}
                  <Col sm={12} xl={6}>
                    <div className="mb-0">
                      <label className="form-label">
                        {t("packages.position")}{" "}
                        <span className="text-muted">
                          ({t("packages.positionDescription")})
                        </span>
                      </label>
                      <Controller
                        name="position"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="number"
                            className={`form-control ${errors.position ? "is-invalid" : ""
                              }`}
                            placeholder={t("packages.position")}
                            {...field}
                          />
                        )}
                      />
                      {errors.position && (
                        <div className="invalid-feedback">
                          {t(errors.position.message as string)}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col sm={2} xl={2} className=" pt-2">
                    <div className="mt-3">
                      <Controller
                        name="isActive"
                        control={control}
                        render={({ field }) => (
                          <Switch
                            id="isActive"
                            label={t("packages.isActive")}
                            className={`${errors.isActive ? 'is-invalid' : ''}  m-0`}
                            checked={field.value}
                            onChange={(checked) => field.onChange(checked)}
                          />
                        )}
                      />
                      {errors.isActive && (
                        <div className="invalid-feedback">
                          {t(errors.isActive.message as string)}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col sm={2} xl={2} className=" pt-2">
                    <div className="mt-3">
                      <Controller
                        name="isRecommended"
                        control={control}
                        render={({ field }) => (
                          <Switch
                            id="isRecommended"
                            label={t("packages.isRecommended")}
                            className={`${errors.isRecommended ? 'is-invalid' : ''} m-0`}
                            checked={field.value}
                            onChange={(checked) => field.onChange(checked)}
                          />
                        )}
                      />
                      {errors.isRecommended && (
                        <div className="invalid-feedback">
                          {t(errors.isRecommended.message as string)}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col sm={12}>
                    <div className="mb-0">
                      <label className="form-label">{t("packages.shortDescription")}</label>
                      <Controller
                        name="shortDescription"
                        control={control}
                        render={({ field }) => (
                          <textarea
                            className={`form-control ${errors.shortDescription ? 'is-invalid' : ''}`}
                            placeholder={t("packages.shortDescription")}
                            rows={3}
                            {...field}
                          />
                        )}
                      />
                      {errors.shortDescription && (
                        <div className="invalid-feedback">
                          {t(errors.shortDescription.message as string)}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col sm={12}>
                    <div className="mb-0">
                      <label className="form-label">{t("packages.description")}</label>
                      <div className={errors.description ? 'is-invalid' : ''}>
                        <Controller
                          name="description"
                          control={control}
                          render={({ field }) => (
                            <Editor
                              apiKey={process.env.REACT_APP_TINYMCE_API_KEY}
                              value={field.value}
                              onEditorChange={(content: string) => field.onChange(content)}
                              init={{
                                height: 360,
                                menubar: false,
                                branding: false,
                                plugins: [
                                  'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                                  'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                                  'insertdatetime', 'media', 'table', 'help', 'wordcount',
                                ],
                                toolbar:
                                  'undo redo | blocks | bold italic underline strikethrough | ' +
                                  'alignleft aligncenter alignright alignjustify | ' +
                                  'bullist numlist outdent indent | table | link image | removeformat | code',
                                content_style: 'body { font-family: Helvetica, Arial, sans-serif; font-size: 14px; }',
                              }}
                            />
                          )}
                        />
                      </div>
                      {errors.description &&
                        <div className="invalid-feedback">
                          {t(errors.description.message as string)}
                        </div>}
                    </div>
                  </Col>
                  {/* Access Rights Section */}
                  {selectedType && (
                    <Col sm={12}>
                      {/* Title with Horizontal Line */}
                      <div className="mb-4">
                        <hr className="text-muted" />
                        <h3 className="text-muted">
                          {t("packages.accessRights")}
                        </h3>
                      </div>

                      {/* Privileges in Three Columns */}
                      <Row>
                        {selectedType === "user" && (
                          <>
                            <Col sm={6} xl={4}>
                              <div className="mb-3">
                                <Controller
                                  name="userSeeReview"
                                  control={control}
                                  render={({ field }) => (
                                    <Switch
                                      id="userSeeReview"
                                      label={t("packages.userSeeReview")}
                                      className={`${errors.userSeeReview ? "is-invalid" : ""
                                        }`}
                                      checked={Boolean(field.value)}
                                      onChange={(checked) =>
                                        field.onChange(checked)
                                      }
                                    />
                                  )}
                                />
                              </div>
                              <div className="mb-3">
                                <Controller
                                  name="userInformOnEmailIfMatchingJobs"
                                  control={control}
                                  render={({ field }) => (
                                    <Switch
                                      id="userInformOnEmailIfMatchingJobs"
                                      label={t(
                                        "packages.userInformOnEmailIfMatchingJobs"
                                      )}
                                      className={`${errors.userInformOnEmailIfMatchingJobs
                                        ? "is-invalid"
                                        : ""
                                        }`}
                                      checked={Boolean(field.value)}
                                      onChange={(checked) =>
                                        field.onChange(checked)
                                      }
                                    />
                                  )}
                                />
                              </div>
                            </Col>
                            <Col sm={6} xl={4}>
                              <div className="mb-3">
                                <Controller
                                  name="userHaveVideoPresentation"
                                  control={control}
                                  render={({ field }) => (
                                    <Switch
                                      id="userHaveVideoPresentation"
                                      label={t(
                                        "packages.userHaveVideoPresentation"
                                      )}
                                      className={`${errors.userHaveVideoPresentation
                                        ? "is-invalid"
                                        : ""
                                        }`}
                                      checked={Boolean(field.value)}
                                      onChange={(checked) =>
                                        field.onChange(checked)
                                      }
                                    />
                                  )}
                                />
                              </div>
                              <div className="mb-3">
                                <Controller
                                  name="userShowFittingJobs"
                                  control={control}
                                  render={({ field }) => (
                                    <Switch
                                      id="userShowFittingJobs"
                                      label={t("packages.userShowFittingJobs")}
                                      className={`${errors.userShowFittingJobs
                                        ? "is-invalid"
                                        : ""
                                        }`}
                                      checked={Boolean(field.value)}
                                      onChange={(checked) =>
                                        field.onChange(checked)
                                      }
                                    />
                                  )}
                                />
                              </div>
                            </Col>
                            <Col sm={6} xl={4}>
                              <div className="mb-3">
                                <Controller
                                  name="userShowJobHistory"
                                  control={control}
                                  render={({ field }) => (
                                    <Switch
                                      id="userShowJobHistory"
                                      label={t("packages.userShowJobHistory")}
                                      className={`${errors.userShowJobHistory ? "is-invalid" : ""}`}
                                      checked={Boolean(field.value)}
                                      onChange={(checked) => field.onChange(checked)}
                                    />
                                  )}
                                />
                              </div>
                              <div className="mb-3">
                                <Controller
                                  name="userFeaturedInCategoriesAndLocations"
                                  control={control}
                                  render={({ field }) => (
                                    <Switch
                                      id="userFeaturedInCategoriesAndLocations"
                                      label={t("packages.userFeaturedInCategoriesAndLocations")}
                                      className={`${errors.userFeaturedInCategoriesAndLocations ? "is-invalid" : ""}`}
                                      checked={Boolean(field.value)}
                                      onChange={(checked) => field.onChange(checked)}
                                    />
                                  )}
                                />
                              </div>
                              <div className="mb-3">
                                <Controller
                                  name="userCanUploadDocuments"
                                  control={control}
                                  render={({ field }) => (
                                    <Switch
                                      id="userCanUploadDocuments"
                                      label={t("packages.userCanUploadDocuments")}
                                      className={`${errors.userCanUploadDocuments ? "is-invalid" : ""}`}
                                      checked={Boolean(field.value)}
                                      onChange={(checked) => field.onChange(checked)}
                                    />
                                  )}
                                />
                              </div>
                            </Col>
                          </>
                        )}

                        {selectedType === "company" && (
                          <>
                            <Col sm={6} xl={4}>
                              <div className="mb-3">
                                <Controller
                                  name="companyCanCommunicateWithCoach"
                                  control={control}
                                  render={({ field }) => (
                                    <Switch
                                      id="companyCanCommunicateWithCoach"
                                      label={t(
                                        "packages.companyCanCommunicateWithCoach"
                                      )}
                                      className={`${errors.companyCanCommunicateWithCoach
                                        ? "is-invalid"
                                        : ""
                                        }`}
                                      checked={Boolean(field.value)}
                                      onChange={(checked) =>
                                        field.onChange(checked)
                                      }
                                    />
                                  )}
                                />
                              </div>
                              <div className="mb-3">
                                <Controller
                                  name="companySeeReview"
                                  control={control}
                                  render={({ field }) => (
                                    <Switch
                                      id="companySeeReview"
                                      label={t("packages.companySeeReview")}
                                      className={`${errors.companySeeReview
                                        ? "is-invalid"
                                        : ""
                                        }`}
                                      checked={Boolean(field.value)}
                                      onChange={(checked) =>
                                        field.onChange(checked)
                                      }
                                    />
                                  )}
                                />
                              </div>
                              <div className="mb-3">
                                <Controller
                                  name="companySeeCoachesHistory"
                                  control={control}
                                  render={({ field }) => (
                                    <Switch
                                      id="companySeeCoachesHistory"
                                      label={t("packages.companySeeCoachesHistory")}
                                      className={`${errors.companySeeCoachesHistory
                                        ? "is-invalid"
                                        : ""
                                        }`}
                                      checked={Boolean(field.value)}
                                      onChange={(checked) =>
                                        field.onChange(checked)
                                      }
                                    />
                                  )}
                                />
                              </div>
                            </Col>
                            <Col sm={6} xl={4}>
                              <div className="mb-3">
                                <Controller
                                  name="companyCanAddLogo"
                                  control={control}
                                  render={({ field }) => (
                                    <Switch
                                      id="companyCanAddLogo"
                                      label={t("packages.companyCanAddLogo")}
                                      className={`${errors.companyCanAddLogo
                                        ? "is-invalid"
                                        : ""
                                        }`}
                                      checked={Boolean(field.value)}
                                      onChange={(checked) =>
                                        field.onChange(checked)
                                      }
                                    />
                                  )}
                                />
                              </div>

                              <div className="mb-3">
                                <Controller
                                  name="companyCanCopyJob"
                                  control={control}
                                  render={({ field }) => (
                                    <Switch
                                      id="companyCanCopyJob"
                                      label={t("packages.companyCanCopyJob")}
                                      className={`${errors.companyCanCopyJob ? "is-invalid" : ""}`}
                                      checked={Boolean(field.value)}
                                      onChange={(checked) => field.onChange(checked)}
                                    />
                                  )}
                                />
                                {errors.companyCanCopyJob && (
                                  <div className="invalid-feedback">
                                    {errors.companyCanCopyJob.message}
                                  </div>
                                )}
                              </div>

                                <div className="mb-3">
                                    <Controller
                                        name="allowedMultiposting"
                                        control={control}
                                        render={({ field }) => (
                                            <Switch
                                                id="allowedMultiposting"
                                                label={t("packages.allowedMultiposting")}
                                                className={`${errors.allowedMultiposting ? "is-invalid" : ""}`}
                                                checked={Boolean(field.value)}
                                                onChange={(checked) => field.onChange(checked)}
                                            />
                                        )}
                                    />
                                    {errors.allowedMultiposting && (
                                        <div className="invalid-feedback">
                                            {errors.allowedMultiposting.message}
                                        </div>
                                    )}
                                </div>

                              <div className="mb-3">
                                <Controller
                                  name="companyCanRequestDocuments"
                                  control={control}
                                  render={({ field }) => (
                                    <Switch
                                      id="companyCanRequestDocuments"
                                      label={t("packages.companyCanRequestDocuments")}
                                      className={`${errors.companyCanRequestDocuments ? "is-invalid" : ""}`}
                                      checked={Boolean(field.value)}
                                      onChange={(checked) => field.onChange(checked)}
                                    />
                                  )}
                                />
                              </div>

                            </Col>
                            <Col sm={6} xl={4}>

                              <div className="mb-3">
                                <label className="form-label">
                                  {t("packages.companyMaxJobHistory")}
                                </label>
                                <Controller
                                  name="companyMaxJobHistory"
                                  control={control}
                                  render={({ field }) => (
                                    <input
                                      type="number"
                                      className={`form-control ${errors.companyMaxJobHistory ? "is-invalid" : ""
                                        }`}
                                      placeholder={t("packages.companyMaxJobHistory")}
                                      {...field}
                                    />
                                  )}
                                />
                                {errors.companyMaxJobHistory && (
                                  <div className="invalid-feedback">
                                    {t(errors.companyMaxJobHistory.message as string)}
                                  </div>
                                )}
                              </div>

                              <div className="mb-3">
                                <label className="form-label">
                                  {t("packages.companyMaxUsers")}
                                </label>
                                <Controller
                                  name="companyMaxUsers"
                                  control={control}
                                  render={({ field }) => (
                                    <input
                                      type="number"
                                      className={`form-control ${errors.companyMaxUsers
                                        ? "is-invalid"
                                        : ""
                                        }`}
                                      placeholder={t(
                                        "packages.companyMaxUsers"
                                      )}
                                      {...field}
                                    />
                                  )}
                                />
                                {errors.companyMaxUsers && (
                                  <div className="invalid-feedback">
                                    {t(errors.companyMaxUsers.message as string)}
                                  </div>
                                )}
                              </div>
                              <div className="mb-3">
                                <label className="form-label">
                                  {t("packages.companyMaxFeaturedJobs")}
                                </label>
                                <Controller
                                  name="companyMaxFeaturedJobs"
                                  control={control}
                                  render={({ field }) => (
                                    <input
                                      type="number"
                                      className={`form-control ${errors.companyMaxFeaturedJobs
                                        ? "is-invalid"
                                        : ""
                                        }`}
                                      placeholder={t(
                                        "packages.companyMaxFeaturedJobs"
                                      )}
                                      {...field}
                                    />
                                  )}
                                />
                                {errors.companyMaxFeaturedJobs && (
                                  <div className="invalid-feedback">
                                    {t(errors.companyMaxFeaturedJobs.message as string)}
                                  </div>
                                )}
                              </div>
                              <div className="mb-3">
                                <label className="form-label">
                                  {t("packages.companyMaxActiveJobs")}
                                </label>
                                <Controller
                                  name="companyMaxActiveJobs"
                                  control={control}
                                  render={({ field }) => (
                                    <input
                                      type="number"
                                      className={`form-control ${errors.companyMaxActiveJobs
                                        ? "is-invalid"
                                        : ""
                                        }`}
                                      placeholder={t(
                                        "packages.companyMaxActiveJobs"
                                      )}
                                      {...field}
                                    />
                                  )}
                                />
                                {errors.companyMaxActiveJobs && (
                                  <div className="invalid-feedback">
                                    {t(errors.companyMaxActiveJobs.message as string)}
                                  </div>
                                )}
                              </div>

                            </Col>

                          </>
                        )}
                      </Row>
                    </Col>
                  )}
                  <Col sm={12}>
                    <FormDropzone onFileUpload={handleFileUpload} existingFiles={getValues('pictures')} />
                    {errors.pictures && <div className="invalid-feedback d-block">{t("blog.pictureRequired")}</div>}
                  </Col>
                  <Col sm={12}>
                    <Button type="submit" variant="primary">
                      {t(`packages.${isEdit ? "update" : "create"}`)}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default PackagesForm;
